import { formatPhone, LIFECYCLE_STAGES, type ContactSummary } from "@tlcrm/shared";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useContacts } from "../api/hooks";
import { DataTable, type Column } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { DncTag, LifecyclePill } from "../components/ui";
import { when } from "../lib/format";

const columns: Column<ContactSummary>[] = [
  {
    header: "Name",
    cell: (c) => (
      <span className="primary">
        {c.fullName} {c.doNotCall && <DncTag />}
      </span>
    ),
  },
  { header: "Stage", cell: (c) => <LifecyclePill stage={c.lifecycleStage} /> },
  {
    header: "Phone",
    cell: (c) =>
      c.primaryPhone ? (
        <span className="mono">{formatPhone(c.primaryPhone)}</span>
      ) : (
        <span className="t3">–</span>
      ),
  },
  { header: "Email", cell: (c) => c.email ?? <span className="t3">–</span> },
  { header: "State", cell: (c) => c.state ?? <span className="t3">–</span>, width: 64 },
  { header: "Owner", cell: (c) => c.owner?.fullName ?? <span className="t3">Unassigned</span> },
  { header: "Updated", cell: (c) => <span className="sub">{when(c.updatedAt)}</span>, num: true },
];

export function Contacts() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const stage = params.get("stage") ?? "";
  const owner = params.get("owner") ?? "";

  // Type, pause, search: one request per pause rather than per key.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q.trim()) next.set("q", q.trim());
      else next.delete("q");
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(t);
  }, [q, params, setParams]);

  const filters = {
    q: params.get("q") ?? undefined,
    stage: stage || undefined,
    owner: owner || undefined,
  };
  const contacts = useContacts(filters);
  const rows = contacts.data?.pages.flatMap((p) => p.items);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Contacts</h1>
          <p>Every person: applicants, insureds, owners, payors and beneficiaries alike.</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="search">
          <Icon name="search" />
          <input
            className="field"
            placeholder="Name, phone digits or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search contacts"
          />
        </div>
        <select
          className="field"
          value={stage}
          onChange={(e) => setParam("stage", e.target.value)}
          aria-label="Stage"
          disabled={Boolean(filters.q)}
        >
          <option value="">All stages</option>
          {LIFECYCLE_STAGES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <div className="seg" role="tablist" aria-label="Owner">
          {(
            [
              ["", "Everyone"],
              ["me", "Mine"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={l}
              role="tab"
              aria-selected={owner === v}
              onClick={() => setParam("owner", v)}
              disabled={Boolean(filters.q)}
            >
              {l}
            </button>
          ))}
        </div>
        {filters.q && (
          <span className="meta">Searching everyone; filters apply to the full list.</span>
        )}
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        href={(c) => `/contacts/${c.id}`}
        loading={contacts.isPending}
        error={contacts.error}
        onRetry={() => void contacts.refetch()}
        empty={
          filters.q
            ? {
                icon: "search",
                title: `Nobody matches “${filters.q}”`,
                body: "Try the last four digits of their number.",
              }
            : { icon: "users", title: "No contacts yet" }
        }
        hasMore={contacts.hasNextPage}
        loadingMore={contacts.isFetchingNextPage}
        onMore={() => void contacts.fetchNextPage()}
      />
    </div>
  );
}
